import React, { FC, useEffect, useMemo, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { RootState } from 'src/redux/rootReducerType';
import { Certification } from 'src/types/Certification';
import { userService } from 'src/services/User.service';
import { getUserAvatarColors } from 'src/components/NewRenterInterface/utils/avatarColors';
import { canManageHousehold } from 'src/components/NewRenterInterface/AccountHub/accountHubUtils';
import MemberDocsModal from './MemberDocsModal';

const SignatureBanner: FC = () => {
  const theme = useTheme();
  const user = useSelector(
    (state: RootState) => state.newRenterInterfaceUser.user
  );
  const certifications = useSelector(
    (state: RootState) => state.newRenterInterface.certifications
  ) as Certification[];

  const userHouseholds = useSelector(
    (state: RootState) => state.newRenterInterface.userHouseholds ?? []
  );

  const userId = user?.id ?? 0;

  const canManage = useMemo(() => {
    if (canManageHousehold(userHouseholds)) {
      return true;
    }
    for (const cert of certifications) {
      const me = cert.cert_household?.cert_members?.find(
        (m) => m.user_id === userId && m.is_managing
      );
      if (me) {
        return true;
      }
    }
    return false;
  }, [userHouseholds, certifications, userId]);

  const [collapsed, setCollapsed] = useState(() =>
    certifications.some((c) => c.reminders_already_sent_today === true)
  );
  const [sendStatus, setSendStatus] = useState<
    'idle' | 'sending' | 'success' | 'error'
  >('idle');

  useEffect(() => {
    if (sendStatus === 'success') {
      const t = setTimeout(() => {
        setSendStatus('idle');
      }, 2500);
      return () => clearTimeout(t);
    }
  }, [sendStatus]);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalCertId, setModalCertId] = useState<number | null>(null);
  const [modalMemberUserId, setModalMemberUserId] = useState<number | null>(
    null
  );
  const [modalFirstName, setModalFirstName] = useState('');
  const [modalLastName, setModalLastName] = useState('');
  const [modalAvatarColor, setModalAvatarColor] = useState('');
  const [modalAvatarFgColor, setModalAvatarFgColor] = useState('');
  const [modalInitials, setModalInitials] = useState('');
  const [modalKey, setModalKey] = useState(0);

  const { totalDocs, membersWithMissing, certIdsWithMissing } = useMemo(() => {
    let total = 0;
    const members = new Map<
      number,
      { firstName: string; lastName: string; userId: number; certId: number }
    >();
    const certIds = new Set<number>();
    for (const cert of certifications) {
      if (
        sendStatus !== 'sending' &&
        sendStatus !== 'error' &&
        cert.reminders_already_sent_today === true
      ) {
        continue;
      }
      for (const m of cert.cert_household?.cert_members ?? []) {
        const cnt = m.pending_signature_count ?? 0;
        if (cnt > 0 && m.user_id !== userId) {
          total += cnt;
          certIds.add(cert.id);
          if (!members.has(m.cert_member_id)) {
            members.set(m.cert_member_id, {
              firstName: m.first_name,
              lastName: m.last_name,
              userId: m.user_id ?? 0,
              certId: cert.id,
            });
          }
        }
      }
    }
    return {
      totalDocs: total,
      membersWithMissing: [...members.values()],
      certIdsWithMissing: certIds,
    };
  }, [certifications, userId, sendStatus]);

  const openModal = useCallback(
    (member: {
      firstName: string;
      lastName: string;
      userId: number;
      certId: number;
    }) => {
      const isDark = theme.palette.mode === 'dark';
      const colors = getUserAvatarColors(member.userId, isDark);
      const inits = `${member.firstName?.[0] ?? ''}${
        member.lastName?.[0] ?? ''
      }`.toUpperCase();
      setModalCertId(member.certId);
      setModalMemberUserId(member.userId);
      setModalFirstName(member.firstName);
      setModalLastName(member.lastName);
      setModalAvatarColor(colors.bg);
      setModalAvatarFgColor(colors.fg);
      setModalInitials(inits);
      setModalKey((k) => k + 1);
      setModalOpen(true);
    },
    [theme.palette.mode]
  );

  const handleSendReminders = useCallback(async () => {
    if (sendStatus === 'sending') {
      return;
    }
    setSendStatus('sending');
    try {
      for (const certId of certIdsWithMissing) {
        await userService.sendSignatureReminder(certId);
      }
      setSendStatus('success');
    } catch (e) {
      setSendStatus('error');
      if (process.env.NODE_ENV !== 'production') {
        console.error(e);
      }
    }
  }, [certIdsWithMissing, sendStatus]);

  if (!canManage) {
    return null;
  }

  if (sendStatus === 'success') {
    return (
      <Box
        sx={{
          borderTop: '1px solid',
          borderColor: 'divider',
          mt: 1,
          pt: 1,
          px: 2,
          pb: 1.5,
          bgcolor: 'success.lighter',
          position: 'relative',
        }}
      >
        <Stack direction="row" alignItems="center" gap={1}>
          <CheckCircleOutlineIcon
            sx={{
              fontSize: 20,
              color: 'success.dark',
              flexShrink: 0,
              alignSelf: 'center',
            }}
          />
          <Typography
            variant="body2"
            fontWeight={600}
            color="success.dark"
            sx={{ flex: 1 }}
          >
            Reminders sent
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (sendStatus === 'idle' && membersWithMissing.length === 0) {
    return null;
  }

  const icon = (
    <InfoOutlinedIcon
      sx={{
        fontSize: 20,
        color: 'info.dark',
        flexShrink: 0,
        alignSelf: 'center',
      }}
    />
  );

  const msg = (
    <Typography
      variant="body2"
      fontWeight={600}
      color="info.dark"
      sx={{ flex: 1 }}
    >
      {totalDocs} document{totalDocs === 1 ? '' : 's'} need signatures from
      others
    </Typography>
  );

  return (
    <>
      {sendStatus === 'error' && (
        <Box
          sx={{
            borderTop: '1px solid',
            borderColor: 'divider',
            mt: 1,
            pt: 1,
            px: 2,
            pb: 1.5,
            bgcolor: 'error.lighter',
            position: 'relative',
          }}
        >
          <Stack direction="row" alignItems="center" gap={1}>
            <ErrorOutlineIcon
              sx={{
                fontSize: 20,
                color: 'error.dark',
                flexShrink: 0,
                alignSelf: 'center',
              }}
            />
            <Typography
              variant="body2"
              fontWeight={600}
              color="error.dark"
              sx={{ flex: 1 }}
            >
              Failed to send reminders. Please try again.
            </Typography>
          </Stack>
        </Box>
      )}
      <Box
        sx={{
          borderTop: '1px solid',
          borderColor: 'divider',
          mt: 1,
          pt: 1,
          px: 2,
          pb: 1.5,
          bgcolor: 'info.lighter',
          position: 'relative',
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          gap={1}
          sx={{ mb: collapsed ? 0 : 1 }}
        >
          {icon}
          {msg}
          <IconButton
            size="small"
            onClick={() => setCollapsed((c) => !c)}
            aria-label={collapsed ? 'Expand' : 'Collapse'}
            sx={{ color: 'text.secondary', flexShrink: 0 }}
          >
            {collapsed ? (
              <UnfoldMoreIcon sx={{ fontSize: 16 }} />
            ) : (
              <UnfoldLessIcon sx={{ fontSize: 16 }} />
            )}
          </IconButton>
        </Stack>

        {!collapsed && (
          <Stack direction="row" alignItems="center" gap={0.5} flexWrap="wrap">
            <Stack direction="row" gap={0.5} flexWrap="wrap" sx={{ flex: 1 }}>
              {membersWithMissing.map((m) => (
                <Chip
                  key={m.userId}
                  label={m.firstName}
                  size="small"
                  role="button"
                  tabIndex={0}
                  onClick={() => openModal(m)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      openModal(m);
                    }
                  }}
                  sx={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: 'info.dark',
                    border: '1px solid',
                    borderRadius: '14px',
                    height: 28,
                    '& .MuiChip-label': { px: 1.25 },
                  }}
                />
              ))}
            </Stack>
            <Button
              size="small"
              variant="contained"
              disabled={sendStatus === 'sending'}
              onClick={handleSendReminders}
              sx={{
                textTransform: 'none',
                fontSize: 12,
                fontWeight: 600,
                bgcolor: 'info.dark',
                '&:hover': { bgcolor: 'info.darker' },
                flexShrink: 0,
                ml: 1,
              }}
            >
              {sendStatus === 'sending' ? (
                <CircularProgress size={14} sx={{ color: 'common.white' }} />
              ) : (
                <span>Remind all</span>
              )}
            </Button>
          </Stack>
        )}
      </Box>

      <MemberDocsModal
        key={modalKey}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        certificationId={modalCertId}
        memberUserId={modalMemberUserId}
        memberFirstName={modalFirstName}
        memberLastName={modalLastName}
        avatarColor={modalAvatarColor}
        avatarFgColor={modalAvatarFgColor}
        initials={modalInitials}
      />
    </>
  );
};

export default SignatureBanner;
